import { useState, useEffect } from 'react';
import { Link, useLocation, NavLink } from 'react-router-dom';
import { Menu, X, ChevronDown, Wallet, User, LogOut, History } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import './Header.css';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const location = useLocation();
  const { isAuthenticated, user, logout } = useAuth();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileMenuOpen]);

  const mainNavItems = [
    { path: '/', label: 'Exchange' },
    { path: '/buy', label: 'Buy / Sell' },
    { path: '/market', label: 'Market' },
  ];

  const dropdownMenus = {
    products: {
      label: 'Products',
      items: [
        { path: '/api', label: 'API', desc: 'For developers & businesses' },
        { path: '/widget', label: 'Widget', desc: 'Embed on your website' },
        { path: '/mobile', label: 'Mobile App', desc: 'Trade on the go' },
        { path: '/referral', label: 'Referral Program', desc: 'Earn up to 2% per exchange' },
        { path: '/affiliate', label: 'Affiliate Program', desc: 'For partners & influencers' },
        { path: '/white-label', label: 'White Label', desc: 'Custom branded solution' },
      ]
    },
    company: {
      label: 'Company',
      items: [
        { path: '/about', label: 'About Us', desc: 'Our story & mission' },
        { path: '/careers', label: 'Careers', desc: 'Join our team' },
        { path: '/blog', label: 'Blog', desc: 'News & updates' },
        { path: '/press', label: 'Press Kit', desc: 'Media resources' },
        { path: '/partners', label: 'Partners', desc: 'Our ecosystem' },
      ]
    },
    support: {
      label: 'Support',
      items: [
        { path: '/help', label: 'Help Center', desc: 'FAQs & guides' },
        { path: '/status', label: 'Status Page', desc: 'System status' },
        { path: '/supported-coins', label: 'Supported Coins', desc: '1000+ currencies' },
        { path: '/list-a-coin', label: 'List a Coin', desc: 'Get your token listed' },
        { path: '/bug-bounty', label: 'Bug Bounty', desc: 'Security rewards' },
      ]
    }
  };

  return (
    <header className={`header ${isScrolled ? 'header-scrolled' : ''}`}>
      <div className="header-container">
        <Link to="/" className="header-logo">
          <div className="logo-mark">
            <svg viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg">
              <rect width="40" height="40" rx="12" fill="url(#logo-gradient)"/>
              <path d="M20 10L25 20L20 24L15 20L20 10Z" fill="white"/>
              <path d="M20 24L25 20L20 30L15 20L20 24Z" fill="white" fillOpacity="0.7"/>
              <defs>
                <linearGradient id="logo-gradient" x1="0" y1="0" x2="40" y2="40">
                  <stop stopColor="#2563eb"/>
                  <stop offset="1" stopColor="#06b6d4"/>
                </linearGradient>
              </defs>
            </svg>
          </div>
          <span className="logo-text">Omini<span className="gradient-text">Bridge</span></span>
        </Link>

        <nav className="header-nav">
          {mainNavItems.map((item) => (
            <NavLink
              key={item.path}
              to={item.path}
              className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
            >
              {item.label}
            </NavLink>
          ))}

          {Object.entries(dropdownMenus).map(([key, menu]) => (
            <div 
              key={key}
              className="nav-dropdown"
              onMouseEnter={() => setActiveDropdown(key)}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button className={`nav-link dropdown-trigger ${activeDropdown === key ? 'open' : ''}`}>
                {menu.label}
                <ChevronDown className="dropdown-chevron" />
              </button>
              
              {activeDropdown === key && (
                <div className="dropdown-menu">
                  <div className="dropdown-content">
                    {menu.items.map((item) => (
                      <Link key={item.path} to={item.path} className="dropdown-item">
                        <span className="dropdown-item-label">{item.label}</span>
                        <span className="dropdown-item-desc">{item.desc}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </nav>

        <div className="header-actions">
          {isAuthenticated ? (
            <>
              <Link to="/history" className="nav-btn">
                <History className="nav-btn-icon" />
                <span>History</span>
              </Link>
              <div 
                className="user-menu-dropdown"
                onMouseEnter={() => setShowUserMenu(true)}
                onMouseLeave={() => setShowUserMenu(false)}
              >
                <button className="nav-btn user-btn">
                  <User className="nav-btn-icon" />
                  <span>{user?.name?.split(' ')[0] || 'Account'}</span>
                  <ChevronDown className="dropdown-chevron" size={14} />
                </button>
                {showUserMenu && (
                  <div className="user-dropdown-menu">
                    <div className="user-dropdown-header">
                      <span className="user-name">{user?.name}</span>
                      <span className="user-email">{user?.email}</span>
                    </div>
                    <Link to="/history" className="user-dropdown-item">
                      <History size={16} />
                      Exchange History
                    </Link>
                    <button className="user-dropdown-item logout" onClick={logout}>
                      <LogOut size={16} />
                      Sign Out
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <>
              <Link to="/history" className="nav-btn">
                <History className="nav-btn-icon" />
                <span>History</span>
              </Link>
              <Link to="/login" className="nav-btn">
                <User className="nav-btn-icon" />
                <span>Sign In</span>
              </Link>
              <Link to="/login" className="nav-btn primary">
                <Wallet className="nav-btn-icon" />
                <span>Get Started</span>
              </Link>
            </>
          )}
        </div>

        <button 
          className="mobile-menu-toggle"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
        >
          {isMobileMenuOpen ? <X /> : <Menu />}
        </button>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="mobile-menu">
          <div className="mobile-menu-content">
            <div className="mobile-nav-section">
              <span className="mobile-nav-title">Main</span>
              {mainNavItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  className={({ isActive }) => `mobile-nav-link ${isActive ? 'active' : ''}`}
                >
                  {item.label}
                </NavLink>
              ))}
            </div>

            {Object.entries(dropdownMenus).map(([key, menu]) => (
              <div key={key} className="mobile-nav-section">
                <span className="mobile-nav-title">{menu.label}</span>
                {menu.items.map((item) => (
                  <NavLink
                    key={item.path}
                    to={item.path}
                    className="mobile-nav-link sub"
                  >
                    {item.label}
                  </NavLink>
                ))}
              </div>
            ))}

            <div className="mobile-actions">
              {isAuthenticated ? (
                <>
                  <div className="mobile-user-info">
                    <User className="btn-icon" />
                    <span>{user?.name || user?.email}</span>
                  </div>
                  <Link to="/history" className="mobile-btn secondary">
                    <History className="btn-icon" />
                    Exchange History
                  </Link>
                  <button className="mobile-btn secondary" onClick={logout}>
                    <LogOut className="btn-icon" />
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <Link to="/history" className="mobile-btn secondary">
                    <History className="btn-icon" />
                    Exchange History
                  </Link>
                  <Link to="/login" className="mobile-btn primary">
                    <User className="btn-icon" />
                    Sign In / Register
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
